
"use client";

import { useRouter } from "next/navigation";
import { useState, type FormEvent } from "react";
import { ArrowRight, LockKeyhole, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (loading) return;

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error ?? "Login failed.");
      }

      if (data.requiresTwoFactor && data.challengeId) {
        sessionStorage.setItem(
            "loginChallengeId",
            data.challengeId
        );

        setPassword("");
        router.push("/verify");
        }
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Unable to sign in."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#101215] flex items-center justify-center px-4 py-10 text-white">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-9">
          <div className="w-14 h-14 rounded-2xl bg-[#22272e] border border-white/10 flex items-center justify-center mb-5">
            <ShieldCheck className="w-7 h-7 text-blue-300" />
          </div>

          <h1 className="text-2xl font-semibold tracking-tight">
            Server Dashboard
          </h1>

          <p className="text-sm text-gray-500 mt-2">
            Sign in to manage your server
          </p>
        </div>

        <div className="bg-[#181b1f] border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Email address
              </label>

              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="admin@example.com"
                className="w-full rounded-xl border border-white/10 bg-[#101215] px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none focus:border-blue-400/50 focus:ring-2 focus:ring-blue-400/10 transition"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-300 mb-2"
              >
                Password
              </label>

              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                className="w-full rounded-xl border border-white/10 bg-[#101215] px-4 py-3 text-sm text-white placeholder:text-gray-600 outline-none focus:border-blue-400/50 focus:ring-2 focus:ring-blue-400/10 transition"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-400">
                {error}
              </p>
            )}

            {message && (
              <p role="status" className="text-sm text-blue-300">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-[#e7edf5] hover:bg-white text-[#111827] py-3 font-semibold text-sm flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Continue"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-600">
          <LockKeyhole className="w-3.5 h-3.5" />
          Secure access · Email verification required
        </div>
      </div>
    </main>
  );
}
